/**
 * Helpers for the contentEditable Doc/LOA editors.
 *
 *  cleanPastedHtml(html) — normalize Word / Google-Docs / browser HTML so
 *  formatting (bold, italics, bullets, numbered lists, dividers) survives the
 *  trip into our editor → server → PDF/DOCX pipeline.
 *
 *  Strategy:
 *    1. Strip Office cruft (comments, styles, xml-namespace tags).
 *    2. Reconstruct Word's "list paragraphs" (MsoListParagraph etc.) into real
 *       <ul>/<ol><li> structure — Word never emits <ul>/<li> on copy.
 *    3. Convert <span style="font-weight:bold">…</span> → <b>…</b> (likewise
 *       italic + underline) so the backend HTML parser picks them up.
 *    4. Strip class/lang/data attributes and remaining style attrs (except on
 *       img/table cells where width/alignment matter).
 */
export function cleanPastedHtml(html) {
  if (!html) return "";
  let s = html;

  // 1) Strip the obvious cruft
  s = s.replace(/<!--[\s\S]*?-->/g, "");
  s = s.replace(/<\?xml[\s\S]*?\?>/gi, "");
  s = s.replace(/<o:p>[\s\S]*?<\/o:p>/gi, "");
  s = s.replace(/<style[\s\S]*?<\/style>/gi, "");
  s = s.replace(/<meta[^>]*>/gi, "");
  s = s.replace(/<link[^>]*>/gi, "");
  s = s.replace(/<\/?(html|body|head|title|script)[^>]*>/gi, "");

  // 2) Detect Word / Google-Docs "list paragraphs" and rebuild as <ul>/<ol>.
  s = reconstructLists(s);

  // 3) Stack-based span/font → b/i/u conversion (preserves Word's bold spans)
  const out = [];
  const stack = [];
  const re = /<(\/?)(span|font)([^>]*)>/gi;
  let lastIndex = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    out.push(s.slice(lastIndex, m.index));
    lastIndex = re.lastIndex;
    const closing = !!m[1];
    if (!closing) {
      const attrs = m[3] || "";
      const styleM = attrs.match(/style\s*=\s*(["'])(.*?)\1/i);
      const style = styleM ? styleM[2].toLowerCase() : "";
      const wraps = [];
      const fw = style.match(/font-weight\s*:\s*([^;]+)/);
      if (fw) {
        const w = fw[1].trim();
        if (w === "bold" || w === "bolder" || (/^\d+$/.test(w) && parseInt(w, 10) >= 600)) wraps.push("b");
      }
      if (/font-style\s*:\s*italic/.test(style)) wraps.push("i");
      if (/text-decoration[a-z-]*\s*:[^;]*underline/.test(style)) wraps.push("u");
      stack.push(wraps);
      out.push(wraps.map((t) => `<${t}>`).join(""));
    } else {
      const wraps = stack.length ? stack.pop() : [];
      out.push(wraps.slice().reverse().map((t) => `</${t}>`).join(""));
    }
  }
  out.push(s.slice(lastIndex));
  s = out.join("");

  // 4) Word also wraps boldness on plain `<p style="font-weight:bold">…</p>`.
  // Normalize <strong>/<em> aliases.
  s = s.replace(/<strong\b[^>]*>/gi, "<b>").replace(/<\/strong>/gi, "</b>");
  s = s.replace(/<em\b[^>]*>/gi, "<i>").replace(/<\/em>/gi, "</i>");

  // 5) Convert paragraphs that have a bottom border style into <hr> (Word's
  // typical "horizontal line" representation).
  s = s.replace(/<p([^>]*)style="[^"]*border-bottom:[^"]*"[^>]*>\s*(?:&nbsp;|\s)*<\/p>/gi, "<hr>");
  s = s.replace(/<div([^>]*)style="[^"]*border-bottom:[^"]*"[^>]*>\s*(?:&nbsp;|\s)*<\/div>/gi, "<hr>");

  // 6) Strip class/lang/dir/id/data attrs on every tag
  s = s.replace(/\s(class|lang|dir|id|data-[\w-]+)="[^"]*"/gi, "");
  // 6a) Strip `style` from every tag EXCEPT img/table (preserve width/height/alignment)
  s = s.replace(/<([a-z][\w-]*)([^>]*)>/gi, (match, tag, attrs) => {
    const t = tag.toLowerCase();
    if (t === "img" || t === "table" || t === "td" || t === "th" || t === "tr") return match;
    return `<${tag}${attrs.replace(/\sstyle="[^"]*"/gi, "")}>`;
  });

  // 7) Drop <font> wrappers entirely (their content is preserved)
  s = s.replace(/<\/?font[^>]*>/gi, "");

  return s;
}

/**
 * Walk through the HTML and convert consecutive "Word/Google-Docs list paragraphs"
 * into real <ul>/<ol><li> structure. Two heuristics:
 *
 *   (a) Word uses `<p class="MsoListParagraph">` or `<p class="MsoListParagraphCxSpFirst">`.
 *       Bullet char (•, ·, o) or "1." is embedded in a leading <span>.
 *   (b) Google Docs uses <ul><li> directly (already fine) OR sometimes
 *       <p style="margin-left: …"> with bullet inside.
 */
function reconstructLists(html) {
  // Use a temporary DOM to walk safely without breaking attribute boundaries.
  const tpl = document.createElement("div");
  tpl.innerHTML = html;

  const isListPara = (el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el.tagName !== "P" && el.tagName !== "DIV") return false;
    const cls = (el.getAttribute("class") || "").toLowerCase();
    if (/msolistparagraph|listparagraph/.test(cls)) return true;
    // Google Docs uses an inline 'list-style-type' or a leading bullet char
    const style = (el.getAttribute("style") || "").toLowerCase();
    if (/list-style-type\s*:/.test(style)) return true;
    return false;
  };

  const detectKind = (el) => {
    // If the first text/markup begins with a digit-and-dot it's ordered, else
    // assume unordered.
    const txt = (el.textContent || "").trimStart();
    if (/^\d+[.)]\s/.test(txt)) return "ol";
    return "ul";
  };

  const stripLeadingMarker = (el) => {
    // Walk through and remove the bullet-marker span Word inserts at the start
    // (it usually contains "·" / "•" / "o" / "§" / "1.").
    while (el.firstChild) {
      const n = el.firstChild;
      if (n.nodeType === 3) {
        // text node — strip leading bullet glyphs + whitespace
        n.nodeValue = n.nodeValue.replace(/^[\s\u00a0]*(?:[•·▪◦‣⁃§o○]+|\d+[.)])[\s\u00a0]+/u, "");
        if (n.nodeValue) break;
        el.removeChild(n);
        continue;
      }
      if (n.nodeType === 1 && /^(span|font|b|i)$/i.test(n.tagName) && /^[\s\u00a0]*(?:[•·▪◦‣⁃§o○]+|\d+[.)])[\s\u00a0]*$/u.test(n.textContent || "")) {
        // Whole inline node is just the bullet marker → drop it.
        el.removeChild(n);
        continue;
      }
      break;
    }
  };

  const children = Array.from(tpl.children);
  let i = 0;
  while (i < children.length) {
    const el = children[i];
    if (!isListPara(el)) { i++; continue; }
    const kind = detectKind(el);
    const items = [];
    let j = i;
    while (j < children.length && isListPara(children[j]) && detectKind(children[j]) === kind) {
      const cur = children[j];
      stripLeadingMarker(cur);
      const li = document.createElement("li");
      // Copy inner nodes (this preserves <b>/<i>/<u>/<a>/etc.)
      while (cur.firstChild) li.appendChild(cur.firstChild);
      items.push(li);
      j++;
    }
    if (items.length === 0) { i++; continue; }
    const list = document.createElement(kind);
    items.forEach((li) => list.appendChild(li));
    tpl.insertBefore(list, el);
    // Remove the original paragraphs we just merged.
    for (let k = i; k < j; k++) {
      try { tpl.removeChild(children[k]); } catch (_) {/* already detached */}
    }
    // Rebuild children list after mutation.
    const rebuilt = Array.from(tpl.children);
    i = rebuilt.indexOf(list) + 1;
    // Replace `children` so the rest of the loop sees the up-to-date snapshot.
    children.length = 0;
    rebuilt.forEach((c) => children.push(c));
  }

  return tpl.innerHTML;
}

/**
 * Wire this into a contentEditable `onPaste` handler:
 *   onPaste={(e) => handleEditorPaste(e)}
 */
export function handleEditorPaste(e) {
  const html = e.clipboardData?.getData("text/html");
  const text = e.clipboardData?.getData("text/plain");
  if (html) {
    e.preventDefault();
    try { document.execCommand("styleWithCSS", false, false); } catch (_) {}
    document.execCommand("insertHTML", false, cleanPastedHtml(html));
  } else if (text) {
    e.preventDefault();
    // Plain text: respect newlines as paragraph breaks
    const paras = text.split(/\r?\n\r?\n/).map((p) => `<p>${escapeHtml(p).replace(/\r?\n/g, "<br>")}</p>`).join("");
    document.execCommand("insertHTML", false, paras);
  }
}

function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
